import React, { useState, useMemo, useEffect } from 'react';
import { useLocalCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { clarityEvent } from '../utils/clarity';
import './LocalCartPage.css';

const fmt = (n) => n != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
    : '—';

const INTERNAL_ROLES = ['ADMIN', 'CURATOR', 'EMPLOYEE'];

const LocalCartPage = () => {
    const { cartItems, updateQuantity, removeFromCart, clearCart } = useLocalCart();
    const { user } = useAuth();
    const navigate  = useNavigate();
    const isInternal = user && INTERNAL_ROLES.includes(user.role);

    const [name,           setName]           = useState(user?.username || '');
    const [email,          setEmail]          = useState(user?.email || '');
    const [companyName,    setCompanyName]    = useState('');
    const [phone,          setPhone]          = useState('');
    const [gstin,          setGstin]          = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const [notes,          setNotes]          = useState('');

    const [isSubmitting,    setIsSubmitting]    = useState(false);
    const [error,           setError]           = useState('');
    const [successCartName, setSuccessCartName] = useState(null);

    useEffect(() => {
        if (cartItems.length > 0) clarityEvent('cart_viewed');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { totalBase, totalGst, totalAmount } = useMemo(() => {
        return cartItems.reduce((acc, item) => {
            const unitPriceInclGst = item.marketOperatingPriceWithGst || item.mrp || 0;
            const unitBasePrice    = item.marketOperatingPrice || (item.mrp ? item.mrp / (1 + (item.gst || 0) / 100) : 0);
            const qty = item.quantity || 1;
            const itemTotal    = unitPriceInclGst * qty;
            const itemBase     = unitBasePrice * qty;
            return {
                totalBase:   acc.totalBase   + itemBase,
                totalGst:    acc.totalGst    + (itemTotal - itemBase),
                totalAmount: acc.totalAmount + itemTotal,
            };
        }, { totalBase: 0, totalGst: 0, totalAmount: 0 });
    }, [cartItems]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user && !email.trim()) { setError('Email is required for guests.'); return; }
        if (cartItems.length === 0) { setError('Your cart is empty.'); return; }

        setIsSubmitting(true);
        setError('');
        try {
            const variantLines = cartItems
                .filter(i => i._variantLabel)
                .map(i => `  - ${i.name}: ${i._variantLabel}`);
            const variantNote = variantLines.length > 0
                ? `\n\nSelected variants:\n${variantLines.join('\n')}`
                : '';
            const payload = {
                name, email, companyName, phone, gstin, billingAddress,
                notes: (notes || '') + variantNote,
                items: cartItems.map(i => ({ productId: i.id, quantity: i.quantity })),
            };
            const res = await api.post('/quotes/request', payload);
            setSuccessCartName(res.data.cartName);
            clarityEvent('quote_submitted');
            clearCart();
        } catch {
            setError('Failed to submit quote request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ── Success Screen ─────────────────────────────────────── */
    if (successCartName) {
        return (
            <div className="lcp-success-wrapper">
                <div className="lcp-success-card">
                    <span className="lcp-success__icon">🎉</span>
                    <h2 className="lcp-success__title">Request Submitted!</h2>
                    <p className="lcp-success__body">
                        Our team has received your quote request and will prepare special pricing for you shortly.
                    </p>
                    <div className="lcp-success__tracking">
                        <span className="lcp-success__tracking-label">Your Tracking Number</span>
                        <strong className="lcp-success__tracking-id">{successCartName}</strong>
                    </div>
                    <Link to="/products" className="lcp-success__cta">Continue Browsing</Link>
                </div>
            </div>
        );
    }

    /* ── Main Cart Page ─────────────────────────────────────── */
    return (
        <div className="lcp-wrapper">
            <div className="lcp-header">
                <div className="lcp-header__eyebrow">Shopping</div>
                <h1 className="lcp-header__title">Request a Quote</h1>
            </div>

            <div className="lcp-body">
                {/* ── Left: Cart Items ── */}
                <div className="lcp-panel">
                    <div className="lcp-panel__title">
                        Your Items
                        {cartItems.length > 0 && (
                            <span className="lcp-panel__count">{cartItems.length}</span>
                        )}
                    </div>

                    {cartItems.length === 0 ? (
                        <div className="lcp-empty">
                            <div className="lcp-empty__icon">🛒</div>
                            <p className="lcp-empty__text">
                                Your cart is empty.{' '}
                                <Link to="/products" className="lcp-empty__link">Browse products</Link>
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="lcp-item-list">
                                {cartItems.map(item => (
                                    <div key={item._cartKey || item.id} className="lcp-item">
                                        <div className="lcp-item__img">
                                            {item.thumbnailUrl
                                                ? <img src={item.thumbnailUrl} alt={item.name} />
                                                : <span>📦</span>
                                            }
                                        </div>

                                        <div className="lcp-item__info">
                                            <div className="lcp-item__name">
                                                {item.name}
                                                {item._variantLabel && <span style={{ color: '#666', fontWeight: 400, marginLeft: 6 }}>— {item._variantLabel}</span>}
                                            </div>
                                            <div className="lcp-item__price">
                                                {fmt(item.marketOperatingPriceWithGst || item.mrp)} / unit · incl. GST
                                            </div>
                                            {item.quantity > 1 && (
                                                <div className="lcp-item__subtotal">
                                                    {fmt((item.marketOperatingPriceWithGst || item.mrp) * (item.quantity || 1))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="lcp-item__actions">
                                            <div className="lcp-stepper">
                                                <button
                                                    className="lcp-stepper__btn"
                                                    aria-label={`Decrease quantity of ${item.name}`}
                                                    onClick={() => updateQuantity(item._cartKey || item.id, Math.max(1, (parseInt(item.quantity, 10) || 0) - 1))}
                                                >−</button>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    aria-label={`Quantity of ${item.name}`}
                                                    className="lcp-stepper__input"
                                                    value={item.quantity === '' ? '' : (parseInt(item.quantity, 10) || 0)}
                                                    onChange={(e) => {
                                                        if (e.target.value === '') {
                                                            updateQuantity(item._cartKey || item.id, '');
                                                        } else {
                                                            const val = parseInt(e.target.value, 10);
                                                            if (!isNaN(val)) updateQuantity(item._cartKey || item.id, val);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        if (item.quantity === '' || item.quantity < 1) updateQuantity(item._cartKey || item.id, 1);
                                                    }}
                                                />
                                                <button
                                                    className="lcp-stepper__btn"
                                                    aria-label={`Increase quantity of ${item.name}`}
                                                    onClick={() => updateQuantity(item._cartKey || item.id, (parseInt(item.quantity, 10) || 0) + 1)}
                                                >+</button>
                                            </div>
                                            <button
                                                className="lcp-item__remove"
                                                onClick={() => { clarityEvent('cart_item_removed'); removeFromCart(item._cartKey || item.id); }}
                                                title="Remove item"
                                            >✕</button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Price breakdown */}
                            <div className="lcp-breakdown">
                                <div className="lcp-breakdown__row">
                                    <span>Subtotal (Excl. GST)</span>
                                    <span>{fmt(totalBase)}</span>
                                </div>
                                <div className="lcp-breakdown__row">
                                    <span>Estimated GST</span>
                                    <span>{fmt(totalGst)}</span>
                                </div>
                                <div className="lcp-breakdown__total">
                                    <span>Total Amount</span>
                                    <span className="lcp-breakdown__total-val">{fmt(totalAmount)}</span>
                                </div>
                                <p className="lcp-breakdown__note">
                                    * Final pricing will be negotiated in your official quote.
                                </p>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Right panel: role-aware ── */}
                {isInternal ? (
                    <div className="lcp-panel lcp-internal-panel">
                        <div className="lcp-internal-panel__icon">🎯</div>
                        <h3 className="lcp-internal-panel__title">Create a Customer Cart</h3>
                        <p className="lcp-internal-panel__body">
                            You're logged in as <strong>{user.role.replace(/_/g, ' ')}</strong>. These staged items can be
                            turned into a curated cart for a client — with custom pricing, a shareable link, and access controls.
                        </p>
                        <button
                            className="lcp-internal-panel__cta"
                            disabled={cartItems.length === 0}
                            onClick={() => navigate('/quote-cart', { state: { preloadItems: cartItems } })}
                        >
                            Continue to Cart Builder →
                        </button>
                        {cartItems.length === 0 && (
                            <p className="lcp-internal-panel__hint">Add products from the catalogue first.</p>
                        )}
                        {cartItems.length > 0 && (
                            <button className="lcp-internal-panel__clear" onClick={clearCart}>
                                Clear staged items
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="lcp-panel lcp-form-panel">
                        <div className="lcp-panel__title">Contact Details</div>
                        <p className="lcp-panel__sub">Provide your details to receive an official quote from eYantra.</p>

                        <form onSubmit={handleSubmit}>
                            {!user && (
                                <>
                                    <div className="lcp-field">
                                        <label className="lcp-field__label">Full Name</label>
                                        <input
                                            type="text"
                                            className="lcp-field__input"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="lcp-field">
                                        <label className="lcp-field__label">Email Address *</label>
                                        <input
                                            type="email"
                                            className="lcp-field__input"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            <div className="lcp-field">
                                <label className="lcp-field__label">Company Name</label>
                                <input
                                    type="text"
                                    className="lcp-field__input"
                                    placeholder="e.g. Acme Corp"
                                    value={companyName}
                                    onChange={e => setCompanyName(e.target.value)}
                                />
                            </div>

                            <div className="lcp-field">
                                <label className="lcp-field__label">Phone Number</label>
                                <input
                                    type="tel"
                                    className="lcp-field__input"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                />
                            </div>

                            <div className="lcp-field">
                                <label className="lcp-field__label">Client GSTIN</label>
                                <input
                                    type="text"
                                    className="lcp-field__input"
                                    placeholder="e.g. 22AAAAA0000A1Z5"
                                    maxLength={15}
                                    value={gstin}
                                    onChange={e => setGstin(e.target.value.toUpperCase())}
                                />
                            </div>

                            <div className="lcp-field">
                                <label className="lcp-field__label">Billing Address</label>
                                <textarea
                                    className="lcp-field__input lcp-textarea"
                                    rows="3"
                                    placeholder="Street, City, State, PIN code"
                                    value={billingAddress}
                                    onChange={e => setBillingAddress(e.target.value)}
                                />
                            </div>

                            <div className="lcp-field">
                                <label className="lcp-field__label">Special Requests / Notes</label>
                                <textarea
                                    className="lcp-field__input lcp-textarea"
                                    rows="3"
                                    placeholder="Delivery timelines, customisation needs, branding requirements…"
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                />
                            </div>

                            {error && <div className="lcp-error">{error}</div>}

                            <button
                                type="submit"
                                className="lcp-submit-btn"
                                disabled={isSubmitting || cartItems.length === 0}
                            >
                                {isSubmitting ? 'Submitting…' : '📩 Submit Quote Request'}
                            </button>

                            <p className="lcp-trust-note">
                                Our team reviews every quote request within 1 business day.
                            </p>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LocalCartPage;
